/**
 * `/support/new` — open a ticket.
 *
 * Reachable with `?license=<id>&key=<prefix>` from a license page, which is
 * what makes this a support ticket rather than a contact form: the operator
 * opens it already knowing which key it is about, instead of spending the first
 * reply asking. The two values ride along as hidden context and the form says
 * plainly that they are attached — a form that silently sends more than it
 * shows is a form people stop trusting.
 *
 * The category is a radio list rather than a select, with a sentence under each
 * option. It is the single most useful thing the customer can tell us and a
 * collapsed dropdown gets whatever was first in the list.
 */

import { AttachmentPicker } from "@/components/AttachmentPicker";
import { PageHeader } from "@/components/PageHeader";
import { Marker, Notice, Strip, StripBody, StripHead } from "@/components/deck";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { portalApi, useApiMutation, useApiQuery } from "@/lib/api";
import type { PickedAttachment } from "@/lib/attachments";
import { toOutgoing } from "@/lib/attachments";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  CATEGORY_HINT,
  CATEGORY_LABEL,
  MAX_MESSAGE_LEN,
  MAX_SUBJECT_LEN,
  MIN_MESSAGE_LEN,
  MIN_SUBJECT_LEN,
  TICKET_CATEGORIES,
  type TicketCategory,
} from "../../shared/support";

export function SupportNewRoute() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const licenseId = params.get("license");
  const keyPrefix = params.get("key");

  const [category, setCategory] = useState<TicketCategory>(licenseId ? "license" : "other");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<PickedAttachment[]>([]);

  // Only to name the license in the "attached to" note. A failure here costs
  // the note and nothing else — the ids are still sent.
  const license = useApiQuery(
    [licenseId ?? ""],
    (signal) => portalApi.getLicense(licenseId ?? "", signal),
    { enabled: Boolean(licenseId), suppressToast: true },
  );

  const create = useApiMutation(
    () =>
      portalApi.createTicket({
        subject: subject.trim(),
        message: message.trim(),
        category,
        license_id: licenseId,
        license_key_prefix: keyPrefix,
        attachments: toOutgoing(attachments),
      }),
    {
      suppressToast: true,
      onSuccess: (result) => navigate(`/support/${result.id}`, { replace: true }),
    },
  );

  const subjectOk =
    subject.trim().length >= MIN_SUBJECT_LEN && subject.trim().length <= MAX_SUBJECT_LEN;
  const messageOk =
    message.trim().length >= MIN_MESSAGE_LEN && message.trim().length <= MAX_MESSAGE_LEN;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!subjectOk || !messageOk) return;
    create.mutate(undefined);
  }

  return (
    <>
      <PageHeader
        code="SUP"
        label="New ticket"
        title={
          <>
            Tell us what is <Marker draw>going on</Marker>
          </>
        }
        description="One person reads these. The more specific you are, the fewer rounds it takes."
        actions={
          <Button asChild variant="ghost" size="sm">
            <Link to="/support">
              <ArrowLeft aria-hidden="true" />
              Back
            </Link>
          </Button>
        }
      />

      {licenseId ? (
        <Notice tone="cleared" label="Attached to this ticket" className="mb-6">
          Your {license.data?.product_name ?? "license"}
          {keyPrefix ? (
            <>
              {" "}
              starting <span className="t-data text-ink">{keyPrefix}</span>
            </>
          ) : null}
          , so we can look it up without asking. Nothing about your key is included.
        </Notice>
      ) : null}

      <form onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-6">
          <section aria-labelledby="category-head">
            <Strip bar={false}>
              <StripHead title="What is it about?" as="h2" id="category-head" />
              <fieldset className="border-0 p-0">
                <legend className="sr-only">Category</legend>
                <div>
                  {TICKET_CATEGORIES.map((option) => (
                    <label
                      key={option}
                      className={cn(
                        "flex cursor-pointer items-start gap-3 border-b border-rule px-4 py-3 transition-colors last:border-b-0",
                        category === option ? "bg-marker/10" : "hover:bg-sunk",
                      )}
                    >
                      <input
                        type="radio"
                        name="category"
                        value={option}
                        checked={category === option}
                        onChange={() => setCategory(option)}
                        className="mt-1 shrink-0 accent-[var(--ink)]"
                      />
                      <span className="min-w-0">
                        <span className="block text-[0.9375rem] text-ink">
                          {CATEGORY_LABEL[option]}
                        </span>
                        <span className="mt-0.5 block text-[0.8125rem] leading-snug text-ink-2">
                          {CATEGORY_HINT[option]}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </Strip>
          </section>

          <section aria-labelledby="detail-head">
            <Strip bar={false}>
              <StripHead title="The details" as="h2" id="detail-head" />
              {/* The strip spans the page; what you type into does not. A
                  subject line and a description box stretched to 64rem are
                  harder to write in, not easier — the fields keep a form's
                  measure inside a panel that matches every other one. */}
              <StripBody className="flex max-w-[46rem] flex-col gap-5">
                <div>
                  <label htmlFor="ticket-subject" className="t-field block">
                    Subject
                  </label>
                  <Input
                    id="ticket-subject"
                    name="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Key will not activate on my new PC"
                    maxLength={MAX_SUBJECT_LEN}
                    required
                    autoFocus
                    className="mt-2 h-10 font-sans"
                  />
                </div>

                <div>
                  <label htmlFor="ticket-message" className="t-field block">
                    What is happening
                  </label>
                  <Textarea
                    id="ticket-message"
                    name="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What you did, what you expected, and what happened instead. Any error text is gold."
                    rows={8}
                    maxLength={MAX_MESSAGE_LEN}
                    required
                    className="mt-2"
                  />
                  <div className="mt-1.5 flex justify-between gap-4 text-[0.8125rem] text-ink-2">
                    <span>Error messages and version numbers help more than anything else.</span>
                    <span className="tnum shrink-0">
                      {message.length.toLocaleString("en-US")}/
                      {MAX_MESSAGE_LEN.toLocaleString("en-US")}
                    </span>
                  </div>
                </div>

                <div>
                  <span className="t-field block">Screenshots</span>
                  <div className="mt-2">
                    <AttachmentPicker
                      attachments={attachments}
                      onChange={setAttachments}
                      disabled={create.isPending}
                    />
                  </div>
                </div>
              </StripBody>
            </Strip>
          </section>
        </div>

        {create.error ? (
          <Notice tone="hold" label="Could not open the ticket" className="mt-6">
            {create.error.message}
          </Notice>
        ) : null}

        <div className="mt-6 flex flex-wrap items-center gap-4">
          <Button type="submit" size="lg" disabled={create.isPending || !subjectOk || !messageOk}>
            {create.isPending ? "Sending…" : "Open the ticket"}
          </Button>
          <p className="text-[0.8125rem] leading-snug text-ink-2">
            You will get an email when we reply, and you can answer it straight from your inbox.
          </p>
        </div>
      </form>
    </>
  );
}
